import {getSafely} from 'shared/web';

import type {ActionDefinition, ActionDefinitionObject} from '../types/ActionDefinition';
import type {RowActions} from '../types/RowData';

export type RowActionItem = {
  actionKey: string;
  definition: ActionDefinition;
};

export type RowActionsResult = {
  /** Primary actions — rendered as IconButton in the action column */
  iconActions: RowActionItem[];
  /** Secondary actions — rendered as menu items in the overflow dropdown */
  overflowActions: RowActionItem[];
  /**
   * The `rowClick` action, rendered as a trailing chevron button in the action column
   * (in addition to firing on whole-row click). Null unless the row has a `rowClick`
   * key that is NOT already present in `primary`/`secondary` — see rules below.
   */
  rowClickAction: RowActionItem | null;
};

const EMPTY_RESULT: RowActionsResult = {
  iconActions: [],
  overflowActions: [],
  rowClickAction: null,
};

export function collect(
  keys: readonly string[],
  actionsDefinition: ActionDefinitionObject
): RowActionItem[] {
  const items: RowActionItem[] = [];
  for (const key of keys) {
    const definition = getSafely(actionsDefinition, key);
    if (!definition || !definition.singleAction) {
      continue;
    }
    items.push({actionKey: key, definition});
  }
  return items;
}

/**
 * Pure utility — not a hook. Partitions visible row actions per the API's semantic split:
 *
 * - `rowActions.primary[]`  → iconActions   (rendered as IconButton in the action column)
 * - `rowActions.secondary[]` → overflowActions (rendered as menu items in the overflow dropdown)
 *
 * Actions where `singleAction === false` are excluded (bulk-only actions).
 * Actions whose key is missing from `actionsDefinition` are excluded silently.
 *
 * The `rowActions.rowClick` key is returned separately as `rowClickAction` — rendered as a
 * trailing chevron button while whole-row click still fires it. It is null when the rowClick
 * key is absent, has no valid single-action definition, or already appears in `primary`/
 * `secondary` (rendering it there instead avoids a duplicate button).
 */
export function partitionRowActions(
  rowActions: RowActions,
  actionsDefinition: ActionDefinitionObject
): RowActionsResult {
  const iconActions = collect(rowActions.primary, actionsDefinition);
  const overflowActions = collect(rowActions.secondary, actionsDefinition);
  const rowClickAction = collectRowClick(rowActions, actionsDefinition);

  if (iconActions.length === 0 && overflowActions.length === 0 && !rowClickAction) {
    return EMPTY_RESULT;
  }

  return {iconActions, overflowActions, rowClickAction};
}

function collectRowClick(
  rowActions: RowActions,
  actionsDefinition: ActionDefinitionObject
): RowActionItem | null {
  const key = rowActions.rowClick;
  if (!key || rowActions.primary.includes(key) || rowActions.secondary.includes(key)) {
    return null;
  }
  const definition = getSafely(actionsDefinition, key);
  if (!definition || !definition.singleAction) {
    return null;
  }
  return {actionKey: key, definition};
}
